import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import HistoryIcon from '@mui/icons-material/History'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import API from '../../../services/API'

// Landing page for /site-configuration: one card per settings group the user
// can see, who the site admins are, and (for admins) the latest changes.
const ConfigOverview = ({ groups, isSiteAdmin }) => {
  const navigate = useNavigate()
  const [siteAdmins, setSiteAdmins] = useState([])
  const [recentLogs, setRecentLogs] = useState([])

  useEffect(() => {
    API.get('/admin/site-configuration/site-admins').then((res) => {
      if (res.status === 200) setSiteAdmins(res.data)
    })
  }, [])

  useEffect(() => {
    if (!isSiteAdmin) return
    API.get('/admin/site-configuration/activity-log').then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        setRecentLogs(res.data.slice(0, 5))
      }
    })
  }, [isSiteAdmin])

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2}>
        {groups.map((group) => {
          const GroupIcon = group.icon
          return (
            <Grid item xs={12} sm={6} lg={4} key={group.name}>
              <Stack
                gap={1}
                sx={{
                  height: '100%',
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
                  bgcolor: 'background.paper',
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1.5,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'anGreen.main',
                      color: 'anGreen.contrastText',
                      flexShrink: 0,
                    }}
                  >
                    <GroupIcon fontSize="small" />
                  </Box>
                  <Typography variant="body1" fontWeight={800}>
                    {group.name}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {group.description}
                </Typography>
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}
                >
                  {group.items.map((item) => (
                    <Chip
                      key={item.slug}
                      label={item.label}
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={() =>
                        navigate(`/site-configuration/${item.slug}`)
                      }
                      sx={{ borderRadius: 1.5, fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Stack>
            </Grid>
          )
        })}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={isSiteAdmin ? 6 : 12}>
          <Stack
            gap={1}
            sx={{
              height: '100%',
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="body1" fontWeight={800}>
              Site Administrators
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isSiteAdmin
                ? 'People with full access to this area.'
                : "If you can't find a setting here, one of these people can help."}
            </Typography>
            <Stack gap={0.75} mt={0.5}>
              {siteAdmins.map((admin) => (
                <Stack
                  key={admin._id}
                  direction="row"
                  gap={1}
                  alignItems="center"
                >
                  <Avatar
                    src={admin?.photograph}
                    sx={{ width: 26, height: 26 }}
                  />
                  <Typography variant="body2">
                    {admin.firstName} {admin.lastName}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Grid>

        {isSiteAdmin && (
          <Grid item xs={12} md={6}>
            <Stack
              gap={1}
              sx={{
                height: '100%',
                p: 2,
                borderRadius: 3,
                border: '1px solid rgba(15, 23, 42, 0.08)',
                bgcolor: 'background.paper',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body1" fontWeight={800}>
                  Recent Changes
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => navigate('/site-configuration/activity-log')}
                >
                  Activity Log
                </Button>
              </Stack>
              {recentLogs.length === 0 ? (
                <Stack alignItems="center" gap={0.5} sx={{ py: 2 }}>
                  <HistoryIcon color="disabled" />
                  <Typography variant="caption" color="text.secondary">
                    Configuration changes will appear here.
                  </Typography>
                </Stack>
              ) : (
                <Stack divider={<Divider flexItem />} gap={0.75}>
                  {recentLogs.map((log) => (
                    <Stack key={log._id} gap={0}>
                      <Typography variant="body2" fontWeight={600}>
                        {log.note}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {moment(log.createdAt).fromNow()}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>
    </Stack>
  )
}

export default ConfigOverview
